// ?autodrive (with ?fakewheel): the scripted "careful driver" from the physics
// checks drives a lap, for demo videos. It's a simple controller that needs to
// act at the physics rate (it destabilises at frame rate), so steering and
// pedals are applied per physics step and mirrored onto the simulated G27 each
// frame (the input overlay shows exactly what the car got). Gear changes go
// through the real wheel path: it pulls the simulated paddle.

import type { Car, CarInput } from '../sim/car';
import type { FakeWheel } from '../wheel/wheelManager';
import type { WheelProfile } from '../wheel/bindings';
import { WheelDriver } from '../../scripts/lib/wheelDriver';

/** Frames a paddle is held, and frames before the next pull. */
const PADDLE_HOLD = 3;
const PADDLE_GAP = 3;

export class AutoDrive {
  private readonly driver: WheelDriver;
  private last: CarInput = { steer: 0, throttle: 0, brake: 0 };
  private wantShift = 0;
  private paddle: { button: number; frames: number } | undefined;
  private gap = 0;

  constructor(line: ReadonlyArray<{ x: number; z: number }>, pace = 0.9) {
    this.driver = new WheelDriver(line, pace, undefined, false);
  }

  /** One physics step of driving. */
  step(car: Car, dt: number): CarInput {
    this.last = this.driver.control(car, dt);
    if (this.driver.shift !== 0) this.wantShift = this.driver.shift;
    return this.last;
  }

  /** End of frame: show the inputs on the simulated wheel and pedals, and work the paddles. */
  frameEnd(fake: FakeWheel, profile: WheelProfile): void {
    const s = fake.state;
    const raw = (b: { rest: number; full: number }, v: number): number => b.rest + (b.full - b.rest) * v;
    s.wheel = raw(profile.steer, this.last.steer);
    // Pedal travel through the bindings' dead zones lands back on the driver's value.
    const dz = 0.03;
    const travel = (v: number): number => (v <= 0 ? 0 : dz + v * (1 - 2 * dz));
    if (profile.throttle) s.pedals[profile.throttle.axis - 1] = raw(profile.throttle, travel(this.last.throttle));
    if (profile.brake) s.pedals[profile.brake.axis - 1] = raw(profile.brake, travel(this.last.brake));
    if (this.paddle) {
      if (--this.paddle.frames <= 0) {
        s.buttons[this.paddle.button] = false;
        this.paddle = undefined;
        this.gap = PADDLE_GAP;
      }
    } else if (this.gap > 0) {
      this.gap--;
    } else if (this.wantShift !== 0) {
      const button = this.wantShift > 0 ? profile.buttons.upshift : profile.buttons.downshift;
      if (button !== undefined) {
        s.buttons[button] = true;
        this.paddle = { button, frames: PADDLE_HOLD };
      }
    }
    this.wantShift = 0;
    fake.push();
  }
}
